import { cartState } from "../cart/cart-state";

/**
 * Quantity stepper on the `/cart` page.
 *
 * Writes go through `cart-state.ts`, so the header bubble and the page's own
 * totals update together. The previous implementation posted to
 * `/cart/change.js` directly, told nothing else about the change, and called
 * `location.reload()` whenever a line was removed.
 *
 * Identifies its line by `key` rather than by index: removing a line shifts
 * every subsequent index, so an index-based write could hit the wrong item.
 *
 * Light DOM, like `cart-drawer` and `cart-page`. The styles live in
 * `cart-quantity.scss` with the rest of the theme's, which a shadow root would
 * cut off; see that file for how the boundary it used to provide is replaced.
 */

const TEMPLATE = `
  <div class="cart-quantity-wrapper">
    <div class="cart-quantity-control">
      <button type="button" class="cart-quantity-button cart-quantity-minus" aria-label="Decrease quantity"><span>&minus;</span></button>
      <input class="cart-quantity-input" type="text" inputmode="numeric" value="1" aria-label="Quantity">
      <button type="button" class="cart-quantity-button cart-quantity-plus" aria-label="Increase quantity"><span>+</span></button>
    </div>
    <button type="button" class="cart-quantity-button cart-quantity-remove">Remove</button>
    <div class="cart-quantity-error" hidden></div>
  </div>
`;

class CartQuantityElement extends HTMLElement {
  private key = "";
  private quantity = 1;
  private input: HTMLInputElement | null = null;
  private error: HTMLElement | null = null;
  private unsubscribe: (() => void) | null = null;

  connectedCallback() {
    // Guarded: an element moved rather than created reconnects with its markup
    // already in place, and rebuilding it would drop the customer's input.
    if (!this.querySelector(".cart-quantity-control")) {
      this.innerHTML = TEMPLATE;
    }

    this.input = this.querySelector<HTMLInputElement>(".cart-quantity-input");
    this.error = this.querySelector<HTMLElement>(".cart-quantity-error");

    this.key = this.getAttribute("key") ?? "";
    this.quantity = Number(this.getAttribute("quantity") ?? 1);
    if (this.input) this.input.value = String(this.quantity);

    // Delegated from the element rather than bound per button, so reconnecting
    // cannot double-bind and there is nothing to unbind but these two.
    this.addEventListener("click", this.onClick);
    this.addEventListener("change", this.onChange);
    document.addEventListener("cart:error", this.onError);

    this.unsubscribe = cartState.subscribe((state) => {
      this.querySelectorAll<HTMLButtonElement>(".cart-quantity-button").forEach(
        (button) => button.toggleAttribute("disabled", state.busy),
      );
    });
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.onClick);
    this.removeEventListener("change", this.onChange);
    document.removeEventListener("cart:error", this.onError);
    this.unsubscribe?.();
  }

  private onClick = (event: MouseEvent): void => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest<HTMLButtonElement>(".cart-quantity-button");
    if (!button || button.hasAttribute("disabled")) return;

    if (button.matches(".cart-quantity-minus")) this.changeBy(-1);
    else if (button.matches(".cart-quantity-plus")) this.changeBy(1);
    else if (button.matches(".cart-quantity-remove")) this.setQuantity(0);
  };

  private onChange = (event: Event): void => {
    if (event.target !== this.input || !this.input) return;
    this.setQuantity(Math.max(0, Math.floor(Number(this.input.value) || 0)));
  };

  private onError = (event: Event): void => {
    const { message, key } = (
      event as CustomEvent<{ message: string; key?: string }>
    ).detail;
    // Line-scoped errors belong to one stepper; unscoped ones apply to all.
    if (key && key !== this.key) return;
    if (!this.error) return;

    this.error.textContent = message;
    this.error.hidden = false;
  };

  private changeBy(delta: number) {
    this.setQuantity(Math.max(0, this.quantity + delta));
  }

  private setQuantity(quantity: number) {
    if (quantity === this.quantity || !this.key) return;

    // Nothing changes here — cart-state.ts writes straight to Shopify and
    // waits for the answer, then re-renders this whole element (and every
    // other cart surface) from the server's response. The busy state from
    // `cartState.subscribe` above disables the buttons for the round trip.
    if (this.error) this.error.hidden = true;
    void cartState.setLineQuantity(this.key, quantity);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "cart-quantity": CartQuantityElement;
  }
}

if (!window.customElements.get("cart-quantity")) {
  window.customElements.define("cart-quantity", CartQuantityElement);
}

export {};
